import { ComponentProps, createElement, ReactNode } from 'react';

import { Grid, GridProps } from '@purpur/library';
import classNames from 'classnames';
import deburr from 'lodash/deburr';
import { UAParser } from 'ua-parser-js';

import { CommonModel } from '@telialt/models/src/CommonModel';

import { datadogLogger } from './datadog/datadogLogger';
import { DDComponent } from './datadog/setup';
import { formatErrorLog } from './datadog/utils';
import { isIOS } from './responsive';

declare global {
    interface Window {
        leadSegment?: string;
        mobileInterface?: { postMessage: (message: string) => void };
    }
}

export const isProduction = process.env.NODE_ENV === 'production';

export const getHybrisConfig = (parameter?: string) =>
    parameter ? window.ACC?.config?.[parameter] : window.ACC?.config;

export const getHybrisCSRFToken = () => getHybrisConfig('CSRFToken');

export const isSSR = () => typeof window === 'undefined' || typeof document === 'undefined';

// TODO: remove leadSegment once exManoTeo is not used anymore
// TODO: not falling back to path segment in all cases because there are A LOT OF places using SEGMENT_VALUE from
// packages/@common/src/constants/paths.ts which is not mocked in tests properly, so even if test is about B2B, mocks are still used from B2C
/**
 * @deprecated use `useSegment` or `useHybrisSegment` from `@telialt/utils/src/segment` instead
 */
export const getHybrisSegment = (): CommonModel.Segment => {
    if (process.env.IS_NEXT === 'true' && isSSR() && process.env.NODE_ENV === 'development') {
        throw new Error('getHybrisSegment should not be used in Next.js. Use useHybrisSegment instead.');
    }

    return window.ACC?.segment?.toLowerCase() || window.leadSegment?.toLowerCase();
};

/**
 * ASM - Assisted Service Mode.
 * This is when business people are connecting to the customers accounts in PROD (something like impersonation).
 * Some of the actions are disabled in this mode as per separate requirements,
 * so usually some links or buttons are hidden or disabled when this function returns true.
 *
 * @returns boolean
 */
export const isHybrisAsmMode = () => Boolean(document.getElementById('_asm'));

export const isWebView = () => Boolean(window.ACC?.isWebView || window.isWebView || window.isWebViewNew);

export const isWebPlatform = () => Boolean(window.ACC?.isWebPlatform || window.isWebPlatform);

export const normalizeString = (item: string | number) => {
    return deburr(item.toString().trim().toLowerCase());
};

// Escapes characters which are used by regexp.
export const escapeRegExp = (text: string) => {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const roundNumber = (value: number, decimalsQuantity: number = 2) => {
    const decimalExponent = 10 ** decimalsQuantity;

    return Math.round(value * decimalExponent) / decimalExponent;
};

export const formatNumber = (
    value: number,
    decimalsQuantity: number = 2,
    decimalSeparator: string = ',',
    thousandsSeparator: string = ' ',
    showZeroDecimals = true,
) => {
    const [integer, decimal] = roundNumber(value, decimalsQuantity).toFixed(decimalsQuantity).split('.');
    const sign = integer.startsWith('-') ? '-' : '';
    const digits = sign ? integer.slice(1) : integer;

    const formattedInteger =
        sign +
        digits
            .split('')
            .map((digit, index) => {
                const positionFromEnd = digits.length - index;
                const needsSeparator = positionFromEnd > 1 && positionFromEnd % 3 === 1;

                return needsSeparator ? `${digit}${thousandsSeparator}` : digit;
            })
            .join('');

    if (decimal && !showZeroDecimals && parseInt(decimal, 10) === 0) {
        return formattedInteger;
    }

    return [formattedInteger, decimal].join(decimalSeparator);
};

export const formatDataAmount = (value: number, units = 'GB') => {
    const unitsWithNoDecimals = ['B', 'KB', 'MB'];

    const roundedValue = unitsWithNoDecimals.includes(units?.toUpperCase())
        ? Math.round(value)
        : Math.round((value + Number.EPSILON) * 100) / 100;

    return roundedValue.toLocaleString('lt');
};

export const formatDataAmountWithUnits = (amount?: number, units = 'GB') => {
    const normalizedUnits = units ? units.toUpperCase() : 'GB';
    const unit = amount === 0 ? 'MB' : normalizedUnits;

    return `${formatDataAmount(amount || 0, units)} ${unit}`;
};

export const formatCurrency = (
    amount?: ReactNode,
    decimalSeparator = ',',
    thousandsSeparator = '',
    showZeroDecimals = true,
) => {
    const amountString = amount?.toString() || '';
    const float = parseFloat(amountString);

    return !isNaN(float)
        ? formatNumber(parseFloat(amountString), 2, decimalSeparator, thousandsSeparator, showZeroDecimals)
        : '';
};

export const formatCurrencyWithoutZeroDecimals = (amount?: ReactNode) => {
    return formatCurrency(amount, ',', '', false);
};

export const isCurrencyValueZero = (amount: number) => roundNumber(amount, 2) < 0.01;

export const scrollElementIntoPageView = (
    element: HTMLElement,
    behavior: ScrollBehavior = 'auto',
    position: 'top' | 'center' = 'top',
    offsetTop: number = 0,
    force = false,
) => {
    if (!element) {
        return;
    }

    let offset = 0;

    const elemData = element.getBoundingClientRect();
    const docViewTop = window.pageYOffset;
    const docViewBottom = docViewTop + window.innerHeight;
    const elemTop = elemData.top + docViewTop;

    if (elemTop > docViewTop && elemTop < docViewBottom && !force) {
        return;
    }

    if (position === 'center') {
        offset = (docViewBottom - docViewTop) / 2;
    }

    window.scroll({
        top: elemTop - offset + offsetTop,
        behavior,
    });
};

export const isEmpty = (value: unknown) =>
    !value ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0);

export const isNullOrUndefined = <T>(value: T | null | undefined): value is null | undefined =>
    value === undefined || value === null;
export const isDefined = <T>(value: T | null | undefined): value is T => !isNullOrUndefined(value);

export const cloneDeep = <T>(object: T): T => JSON.parse(JSON.stringify(object ?? null));

export const logError = (error: any, component: DDComponent, customData?: Record<string, unknown>) => {
    // eslint-disable-next-line no-console
    console.error(error);

    const { message, ...errorContext } = formatErrorLog(error);

    const datadogLogs = datadogLogger();
    datadogLogs.warn(message, {
        ...errorContext,
        component,
        ...customData,
    });
};

// Non-cryptographic ID for DOM element keys.
// eslint-disable-next-line sonarjs/pseudo-random
export const getUniqueId = () => '_' + Math.random().toString(36).slice(2, 11);

// Review this helper
export const isVisibleInContainer = (element: HTMLElement, container: HTMLElement, partial: boolean) => {
    let toReturn = null;

    if (!element || !container) {
        return toReturn;
    }

    const cTop = container.scrollTop;
    const cBottom = cTop + container.clientHeight;

    const eTop = element.offsetTop - container.offsetTop;
    const eBottom = eTop + element.clientHeight;

    const isTotal = eTop >= cTop && eBottom <= cBottom;

    const isPartial = partial && ((eTop < cTop && eBottom > cTop) || (eBottom > cBottom && eTop < cBottom));

    if (partial) {
        if (isTotal) {
            toReturn = 'total';
        }

        if (isPartial) {
            toReturn = 'partial';
        }
    }

    return toReturn;
};

export const getHtmlContentSlots = (container: HTMLElement) => {
    const cmsContentSlotIds = [];

    const cmsContentSlotNodes = container.querySelectorAll('* > div[data-dynamic-slot]');

    for (let i = 0; i < cmsContentSlotNodes.length; i++) {
        const item = cmsContentSlotNodes[i] as HTMLElement;

        cmsContentSlotIds.push(item.dataset.dynamicSlot);
    }

    return cmsContentSlotIds;
};

export const secondsToTime = (secs: number) => {
    const divisorForMinutes = secs % (60 * 60);
    const minutes = Math.floor(divisorForMinutes / 60);

    const divisorForSeconds = divisorForMinutes % 60;
    const seconds = Math.floor(divisorForSeconds);

    return { minutes, seconds };
};

export const getPluralByNumber = (num: number, message: string) => {
    let form = 2;

    if (num % 10 === 1 && num % 100 !== 11) {
        form = 0;
    } else if (num % 10 >= 2 && (num % 100 < 10 || num % 100 >= 20)) {
        form = 1;
    }

    return message.split(';')[form];
};

export const triggerInputChange = (input: HTMLInputElement, value: string = '') => {
    // onChange event is not triggered when input value is identical to the value you are trying to set.
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    const nativeInputValueSetter = descriptor?.set;
    if (!nativeInputValueSetter) {
        throw new Error('Unable to get native input value setter');
    }

    let event: Event;

    nativeInputValueSetter.call(input, value);

    if (typeof Event === 'function') {
        event = new Event('change', { bubbles: true });
    } else {
        event = document.createEvent('Event');

        event.initEvent('change', true, true);
    }

    input.dispatchEvent(event);
};

export const getYoutubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);

    if (match && match[2]) {
        return match[2];
    }

    return null;
};

/**
 * Parse message with parameters (e.g. {price}) and replace with a string or react component or function.
 * Supports additional arguments within param (e.g.{url|click here})
 *
 * @param {string} message - parameterized message
 * @param {object} replacePairs - keys should be named with param name. value is string or (i, match, args) => {}
 */
export const msgReplace = (
    message: string,
    replacePairs: {
        [key: string]: ReactNode | ((i: number, match: string, args: string[]) => ReactNode);
    },
): ReactNode => {
    return message.split(/[{}]/g).map((item, i) => {
        const [tag, ...args] = item.split('|');
        const component = replacePairs[tag];

        if (component) {
            if (typeof component === 'function') {
                return component(i, tag, args);
            }

            return component;
        }

        return item;
    });
};

export const isDeepEqual = (value: any, other: any) => JSON.stringify(value) === JSON.stringify(other);

export const triggerSubmit = (form: HTMLFormElement) => {
    let event;

    if (typeof Event === 'function') {
        event = new Event('submit', { cancelable: true, bubbles: true });
    } else {
        event = document.createEvent('Event');
        event.initEvent('submit', true, true);
    }

    form.dispatchEvent(event);
};

type DateFormatType = 'date' | 'time' | 'datetime' | 'month' | 'timeShort' | 'datetimeShort' | 'year';

export const formatDate = (date: string | Date, format: DateFormatType = 'date'): string => {
    if (!date) {
        return '';
    }

    const dateObject = typeof date === 'string' ? new Date(date) : date;

    const year = dateObject.getFullYear().toString();
    const month = padStringStart((dateObject.getMonth() + 1).toString(), 2, '0');
    const monthName = capitalize(
        // IE/Edge adds 0x200E symbol so we need to remove it
        dateObject.toLocaleString('lt', { month: 'long' }).replace(/\u200E/g, ''),
    );
    const day = padStringStart(dateObject.getDate().toString(), 2, '0');
    const hours = padStringStart(dateObject.getHours().toString(), 2, '0');
    const minutes = padStringStart(dateObject.getMinutes().toString(), 2, '0');
    const seconds = padStringStart(dateObject.getSeconds().toString(), 2, '0');

    switch (format) {
        case 'time':
            return `${hours}:${minutes}:${seconds}`;
        case 'datetime':
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        case 'month':
            return monthName;
        case 'timeShort':
            return `${hours}:${minutes}`;
        case 'datetimeShort':
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        case 'year':
            return year;
        case 'date':
        default:
            return `${year}-${month}-${day}`;
    }
};

const formatTestAttribute = (text?: string) => {
    // TODO: when deburr issue in running tests is finished,
    // try to use the call to normalizeString, instead:
    // return normalizeString(text || '00').replace(/[^a-z0-9]/g, '');
    if (!text) {
        return '00';
    }

    var trimmedText = text.toString().trim().toLowerCase();

    trimmedText = trimmedText.normalize('NFD');

    return trimmedText.replace(/[^a-z0-9]/g, '');
};

export type DataTest = {
    dataTest?: string;
};

export const dataTestValue = (patternOrDataTest: string, value: ReactNode = null, dataTest?: string) => {
    if (dataTest) {
        return dataTest;
    }

    if (value) {
        return patternOrDataTest.replace('--', `-${formatTestAttribute(value.toString())}--`);
    }

    return patternOrDataTest;
};

export const getNestedValue = (object: Record<string, any>, properties: string | readonly string[]) => {
    const splitProperties = typeof properties === 'string' ? properties.split('.') : properties;

    return splitProperties.reduce(
        (accumulator, property) => (accumulator && accumulator[property] ? accumulator[property] : null),
        object,
    );
};

export const setNestedValue = (object: Record<string, any>, properties: string | readonly string[], value: string) => {
    const splitProperties = typeof properties === 'string' ? properties.split('.') : [...properties];
    const lastProperty = splitProperties.pop();

    if (!lastProperty) {
        return;
    }

    const reducedObject = splitProperties.reduce((accumulator, property) => {
        if (!accumulator[property]) {
            accumulator[property] = {};
        }

        return accumulator[property];
    }, object);

    reducedObject[lastProperty] = value;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export const withParams = (content: string, params: Record<string, any>) =>
    content?.replace(/\{([^{}]*)\}/g, (_$1, $2: string) => {
        const value = params[$2] ?? '';

        return Object.prototype.hasOwnProperty.call(params, $2) ? value : `{${$2}}`;
    });

export const windowOpen = (url: string) => {
    if (isIOS()) {
        const link = document.createElement('a');
        document.body.appendChild(link);
        link.href = url;
        link.target = '_blank';
        link.click();
        document.body.removeChild(link);
    } else {
        window.open(url);
    }
};

export const registerWebViewNavigateAction = (
    url: string,
    action: 'navigate' | 'download' = 'navigate',
    name?: string,
    responseType?: string,
) => {
    if (!window.mobileInterface) {
        return;
    }

    const json = JSON.stringify({
        action,
        url,
        name,
        responseType,
    });

    window.mobileInterface.postMessage(json);
};

export const formatShortPhoneNumber = (phoneNumber: string) =>
    phoneNumber?.replace(/(\d{3})(\d{2})(\d{3})/, '$1\xa0$2\xa0$3');

/**
 * @deprecated use `formatLithuanianPhoneNumber` instead
 */
export const formatLithuanianPhoneNumberOld = (phoneNumber: string) =>
    phoneNumber.replace(/(\+370)(\d{3})(\d{5})/, '$1\xa0$2\xa0$3');

export const formatLithuanianPhoneNumber = (phoneNumber: string) =>
    (phoneNumber || '').replace(/(\+370|8)(\d{3})(\d{2})(\d{3})/, '$1\xa0$2\xa0$3\xa0$4');

export const getHeaderTopOffset = () => {
    const headerElementHeight = document.getElementsByClassName('header')[0]?.clientHeight || 0;
    const subMenuElementHeight = document.getElementsByClassName('sub-menu')[0]?.clientHeight || 0;

    return headerElementHeight + subMenuElementHeight;
};
export const textToSlug = (text: string) => normalizeString(text).replace(/\s/g, '-').replace(/[()]/g, '');

const padStringStart = (value: string, maxLength: number, fillString: string) => {
    const paddingLength = maxLength - value.length;
    const fillStringCount = Math.ceil(paddingLength / fillString.length);
    const padding = Array(fillStringCount + 1)
        .join(fillString)
        .slice(0, paddingLength);

    return `${padding}${value}`;
};

type GridColSpanClasses = Pick<ComponentProps<typeof Grid.Item>, 'colSpanSm' | 'colSpanMd' | 'colSpanLg'>;
type GridColsClasses = Pick<GridProps, 'colsSm' | 'colsMd' | 'colsLg'>;
const is3inARow = (itemTotal: number) => [1, 2, 3, 5, 6, 9].includes(itemTotal);

export const getGridItemColumns = (itemTotal: number): GridColSpanClasses => ({
    colSpanSm: 4,
    colSpanMd: 4,
    colSpanLg: is3inARow(itemTotal) ? 4 : 3,
});
export const getGridColumns = (itemTotal: number): GridColsClasses => ({
    colsSm: 1,
    colsMd: 2,
    colsLg: is3inARow(itemTotal) ? 3 : 4,
});
export const getCMSTitle = (text?: string, tag?: string, titleClassName?: string) =>
    text &&
    createElement(
        tag && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag) ? tag : 'h2',
        { className: classNames('component__title', titleClassName) },
        text,
    );

export const decodeHTMLEntities = (text: string) => {
    var textArea = document.createElement('textarea');
    textArea.innerHTML = text;

    return textArea.value;
};

export const isAndroidOS = () => {
    const ua = UAParser(window.navigator.userAgent);

    return ua.os.name === 'Android';
};

export const isAppleOS = () => {
    const ua = UAParser(window.navigator.userAgent);

    return ua.os.name === 'iOS';
};

export const isMacOS = () => {
    const ua = UAParser(window.navigator.userAgent);

    return ua.os.name === 'macOS';
};

export const isSafari = () => {
    const ua = UAParser(window.navigator.userAgent);

    return ua.browser.name === 'Safari' || ua.browser.name === 'Mobile Safari';
};

export const isFloat = (num: number) => num % 1 !== 0;

export const isProd = () =>
    isSSR()
        ? process.env.HYBRIS_PUBLIC_URL === 'https://www.telia.lt' ||
          process.env.TOPUP_API_URL === 'https://internal.my.telia.lt'
        : [
              'www.telia.lt',
              'hybris-prod.in.telecom.lt:9002',
              'hybris-prod2.in.telecom.lt:9002',
              'hybris-prod3.in.telecom.lt:9002',
          ].includes((typeof window !== 'undefined' && window.location.host) || '');

export const isPreprod = () =>
    isSSR()
        ? process.env.HYBRIS_PUBLIC_URL === 'https://www-preprod.telia.lt'
        : ['www-preprod.telia.lt', 'hybris-preprod.in.telecom.lt:9002'].includes(
              (typeof window !== 'undefined' && window.location.host) || '',
          );

export const convertPhoneNumberToInternational = (phoneNumber: string) => {
    return phoneNumber.replace(/^0/, '+370');
};

export const removeQueryParam = (paramKey: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(paramKey);
    const newUrl = url.toString();
    window.history.replaceState({}, '', newUrl);
};
